import React, { memo, useCallback, useMemo, useState } from 'react';
import { Tooltip } from '@mui/material';
import {
  DefaultToolbar,
  TldrawUiButtonIcon,
  TldrawUiToolbarButton,
  useEditor,
  useIsToolSelected,
  useReadonly,
  useTools,
  useTranslation,
} from 'tldraw';
import {
  DEFAULT_FRAME_HEIGHT,
  DEFAULT_FRAME_WIDTH,
  addFrameFromContext,
} from '../utils/canvasEmptySpaceActions';
import { createOpsOnlyDocumentSaver } from '../utils/persistBoardDocumentOps';
import { useGeminiLanguage } from '../hooks/useGeminiLanguage';
import { getCanvasMessages } from '../utils/canvasI18n';
import HowToPinModal from './HowToPinModal';

const TOOLTIP_PROPS = {
  enterDelay: 0,
  enterNextDelay: 0,
  arrow: true,
  placement: 'top',
  slotProps: {
    popper: {
      sx: { zIndex: 1400 },
    },
  },
};

const MODE_TOOLS = [
  { id: 'arrow', icon: 'tool-arrow', label: 'Connect the objects' },
  { id: 'text', icon: 'tool-text', label: 'Save your thought (idea, decision, anything)' },
];

function viewportFramePoint(editor) {
  const bounds = editor.getViewportPageBounds?.();
  if (!bounds) return { x: 80, y: 80 };
  const midX = Number.isFinite(bounds.midX) ? bounds.midX : bounds.x + bounds.w / 2;
  const midY = Number.isFinite(bounds.midY) ? bounds.midY : bounds.y + bounds.h / 2;
  return {
    x: midX - DEFAULT_FRAME_WIDTH / 2,
    y: midY - DEFAULT_FRAME_HEIGHT / 2,
  };
}

function ToolbarTooltipButton({ label, icon, pressed = false, testId, onSelect }) {
  return (
    <Tooltip title={label} {...TOOLTIP_PROPS}>
      <TldrawUiToolbarButton
        type="tool"
        aria-label={label}
        aria-pressed={pressed ? 'true' : 'false'}
        data-testid={testId}
        data-value={testId}
        onClick={onSelect}
      >
        <TldrawUiButtonIcon icon={icon} />
      </TldrawUiToolbarButton>
    </Tooltip>
  );
}

function ToggleToolButton({ toolId, icon, label }) {
  const editor = useEditor();
  const tools = useTools();
  const msg = useTranslation();
  const tool = tools[toolId];
  const pressed = useIsToolSelected(tool);
  const tooltip = label || (tool ? msg(tool.label) : toolId);

  const onSelect = useCallback(() => {
    if (!tool) return;
    if (editor.getCurrentToolId() === toolId) {
      editor.updateInstanceState({ isToolLocked: false });
      editor.setCurrentTool('select');
      return;
    }
    tool.onSelect('toolbar');
  }, [editor, tool, toolId]);

  if (!tool) return null;

  return (
    <ToolbarTooltipButton
      label={tooltip}
      icon={tool.icon || icon}
      pressed={pressed}
      testId={toolId}
      onSelect={onSelect}
    />
  );
}

function HelpToolbarButton() {
  const { geminiLanguage } = useGeminiLanguage();
  const messages = getCanvasMessages(geminiLanguage);
  const [open, setOpen] = useState(false);

  return (
    <>
      <ToolbarTooltipButton
        label={messages.howToPinTitle}
        icon="question-mark"
        testId="how-to"
        onSelect={() => setOpen(true)}
      />
      <HowToPinModal open={open} onClose={() => setOpen(false)} messages={messages} />
    </>
  );
}

function GroupFrameToolbarButton() {
  const editor = useEditor();
  const saveSnapshot = useMemo(() => createOpsOnlyDocumentSaver(editor), [editor]);

  const onSelect = useCallback(() => {
    if (editor.getCurrentToolId() !== 'select') {
      editor.updateInstanceState({ isToolLocked: false });
      editor.setCurrentTool('select');
    }
    addFrameFromContext(editor, viewportFramePoint(editor), { saveSnapshot });
  }, [editor, saveSnapshot]);

  return (
    <ToolbarTooltipButton
      label="Group the objects"
      icon="tool-frame"
      testId="group-frame"
      onSelect={onSelect}
    />
  );
}

/** Board tool strip: arrow, text, and the context-menu group frame. */
const BoardCanvasToolbar = memo(function BoardCanvasToolbar() {
  const isReadonly = useReadonly();
  if (isReadonly) return null;

  return (
    <DefaultToolbar>
      {MODE_TOOLS.map(tool => (
        <ToggleToolButton key={tool.id} toolId={tool.id} icon={tool.icon} label={tool.label} />
      ))}
      <GroupFrameToolbarButton />
      <HelpToolbarButton />
    </DefaultToolbar>
  );
});

export default BoardCanvasToolbar;
